<template>
    <div>
        <div class="white-box">
            <transition
                name="opacity"
                mode="out-in"
            >
                <Skeletons v-if="isLoadingData" />
                <div v-else>
                    <ul class="text-tip border-b border-gray-100 mt-1">
                        <li class="mb-3.5">
                            申請單筆增額保費投資，需一併補收當期尚未繳納之計畫保費。
                        </li>
                    </ul>
                    <h4 class="border-b border-dashed border-gray-100 md:border-none pb-2.5 md:pb-0">
                        請輸入本次投資金額
                    </h4>
                    <div class="w-full flex pt-2.5 pb-3.5 mb-3.5 border-b border-gray-200">
                        <div class="w-1/3 max-w-[8.375rem] flex-shrink-0 label-base text-base xs:text-lg md:hidden">
                            本次增額
                        </div>
                        <div class="w-2/3 max-w-[24rem] md:max-w-[24rem]">
                            <input
                                v-model.number="userData.amount"
                                :placeholder="placeholder"
                                type="text"
                                class="input-base text-right md:text-left"
                                inputmode="numeric"
                                autocomplete="off"
                                maxlength="8"
                                @input="validateAmount"
                            >
                            <p class="text-error text-right">
                                {{ userData.errorMessage.amount }}
                            </p>
                        </div>
                    </div>
                    <div>
                        <h4>配置比例</h4>
                        <BankFundForm
                            v-model:validInput="validInput"
                            v-model:subtotal="subtotal"
                            :data="userData.singlePremList"
                            :bankList="bankList"
                            :fundBankList="fundBankList"
                            :autoAddRow="true"
                            :hasTitle="true"
                            @getValidResult="getValidResult"
                        />
                    </div>
                </div>
            </transition>
        </div>
        <Bottombar>
            <template #top>
                <div class="text-xs py-1">
                    剩餘可分配比例：
                </div>
                <div class="text-base xs:text-lg py-1">
                    {{ subtotal }}%
                </div>
            </template>
            <template #center>
                <div class="text-base py-2.5">
                    剩餘可分配比例： {{ subtotal }}%
                </div>
            </template>
            <template #right>
                <button
                    class="btn btn-accent rounded-full"
                    type="button"
                    @click="validInput = true"
                >
                    下一步
                </button>
            </template>
        </Bottombar>
        <Loading :is-loading="isLoading" />
    </div>
</template>

<script>
import { ref, computed, reactive, inject, toRefs } from 'vue';
import { getInfoA5, getFundBankInfoA5, submitA5 , fundCodeInfo } from '@/apis/A5';
import { addComma } from '@/utils/number/addComma';
import { intMinMax } from '@/utils/rules/customRules';
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';
import BankFundForm from '@/components/template/bankFund/BankFundForm.vue';
import Skeletons from './Skeletons.vue';
import dialogContnet from '@/json/dialogContent.js';
import Loading from '@/components/Loading.vue';
import Bottombar from '@/components/layout/Bottombar.vue';
import routeName from '@/router/constant.js';
import { itMatchesOne } from 'daisyui/src/lib/postcss-prefixer/utils';
import InvestDialog from '@/components/dialogs/InvestDialog.vue';

export default {
    components: {
        BankFundForm,
        Skeletons,
        Loading,
        Bottombar,
        InvestDialog
    },
    setup() {
        const $vfm = inject('$vfm');
        const store = useStore();
        const route = useRoute();
        const router = useRouter();
        const { code } = route.meta;
        const state = reactive({
            isSubmissionNote: false,
            isLoading: false,
            isLoadingData: false,
            validInput: false,
        });
        const data = reactive({
            showData: {},
            userData: {
                amount: '',
                singlePremList: [],
                errorMessage: {
                    amount: '',
                }
            },
            bankList: [],
            fundBankList: []
        });
        const subtotal = ref(0);
        const result = computed(() => store.state.services.changeItemsResult[`${code}APPLY`]);
        /** 取得頁面資料 */
        const getData = async () => {
            state.isLoadingData = true;
            /** 投資標的公司及投資標的名稱下拉式選單資料 */
            await getFundBankInfoA5().then(res => {
                data.bankList = res.data.bankList.map(i => {
                    i.value = i.bankNo;
                    return i;
                });
                data.fundBankList = res.data.fundBankList.map(i => {
                    i.value = i.fundCode;
                    i.label = `${i.fundCode} ${i.fundName} (${i.fundTypeCH})`;
                    return i;
                });
            });

            /** user資料 */
            getInfoA5().then((res) => {
                if (res.data) {
                    data.showData = res.data;
                }
            }).finally(() => {
                state.isLoadingData = false;
            });
        };

        if (!result.value) {
            getData();
        } else {
            const { storeFundBankList, storeShowData, storeBankList, storeUserData } = result.value;
            data.bankList = storeBankList;
            data.fundBankList = storeFundBankList;
            data.showData = storeShowData;
            data.userData.amount = storeUserData.amount;
            data.userData.singlePremList = storeUserData.singlePremList;
        }

        const placeholder = computed(() => {
            let str = `至少${addComma(data.showData.amountMin)}`;
            if (data.showData.currency === 'NTD') str = str + '元';
            return str;
        });

        const validateAmount = (event) => {
            const amountMin = data.showData.amountMin;
            const str = data.showData.currency === 'NTD' ? '元' : '';
            const { val, errorMessage } = intMinMax({
                value: event.target.value,
                minNum: amountMin,
                maxNum: 10000000,
                type: 2,
                minMsg: `投資金額須大於${addComma(amountMin)}${str}`,
                maxMsg: '單筆增額投資金額，不可超過1000萬，請重新輸入。',
            });
            data.userData.errorMessage.amount = errorMessage;
            event.target.value = val;
        };

        const getValidResult = (hasFormError) => {
            // hasFormError=false ,繼續檢查其他欄位
            let hasErrorData = hasFormError;
            let params = null;
            if (!hasErrorData) {
                // 確認input是否為空
                if (!data.userData.amount) data.userData.errorMessage.amount = '請輸入本次投資金額';
                hasErrorData = !!data.userData.errorMessage.amount;
                if (hasErrorData) {
                    params = dialogContnet.validateMsg;
                    modalShow(params);
                } else {
                    submit();
                }
            }
        };

        /** 製作只有fundCode percent 物件的陣列 */
        const makeArray = (ary) => {
            const list = ary.map(i => {
                return {
                    fundCode: i.fundCode,
                    percent: i.percent
                };
            });
            return list;
        };

        const submit = () => {
            state.isLoading = true;
            const returnData = {
                amount: 0,
                singlePremList: [],
                ...(state.isSubmissionNote ? { ifPayingMessage: true } : {})
            };
            returnData.amount = data.userData.amount;
            returnData.singlePremList = makeArray(data.userData.singlePremList);

            const fundCode = [...returnData.singlePremList.map(item => ({
                fundCode : item.fundCode,
                kind : 1
            }))];

            const paload = {fundCodeOutList : fundCode}
            fundCodeInfo(paload).then(res => {
                const params = {
                    title: '提示',
                    // content: `基金代號［${str}］不可同時轉入與轉出。`,
                    content : `${res.data.fundCodeMsg}`.replace(/\n/g,"<br>"),
                    confirmButton: '確認',
                    cancelButton: null,
                    closeicon: false,
                }
                const hasFundCodeMsg = res.data.fundCodeMsg && res.data.fundCodeMsg.trim() !== "";
                if(hasFundCodeMsg){
                    modalShow(params,callA5);
                }else{
                    callA5();
                }
            });

            const callA5 = () => {

                submitA5(returnData).then(res => {
                const list = {
                    storeBankList: data.bankList,
                    storeFundBankList: data.fundBankList,
                    storeShowData: data.showData,
                    storeUserData: data.userData,
                };

                if(res.data.submissionNote && !state.isSubmissionNote) {
                    var params = {
                      content: `${res.data.submissionNote}`.replace(/\n/g,"<br>"),
                      confirmButton: '確認',
                      cancelButton: null,
                      closeicon: false
                    };
                    investModalShow(params); // 顯示錯誤提示
                    state.isSubmissionNote = true;
                    return;
                }

                store.dispatch('services/setChangeItemsResult', { code: `${code}APPLY`, value: list });
                router.push({ name: routeName.ONE_TIME_INVEST_SURVEY });
            }).finally(() => {
                state.isLoading = false;
            });
            }

        };

        const modalShow = (params,vl) => {
            $vfm.show({
                component: 'DialogBase',
                params,
                on: {
                    confirm() {
                        $vfm.hide('DialogBase');
                        if(vl())vl();
                    },
                    closed() {
                        $vfm.hide('DialogBase');
                    }
                },
            });
        };

        const investModalShow = (params,vl) => {
            $vfm.show({
                component: 'InvestDialog',
                params,
                on: {
                    confirm() {
                        $vfm.hide('InvestDialog');
                        if(vl())vl();
                    },
                    closed() {
                        $vfm.hide('InvestDialog');
                    }
                },
            });
        };

        return {
            ...toRefs(state),
            ...toRefs(data),
            placeholder,
            subtotal,
            addComma,
            makeArray,
            validateAmount,
            getValidResult
        };
    }
};
</script>
